import React, { useCallback, useMemo } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { GeolocationCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/GeolocationDefinition';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { GeolocationRequest } from 'libs/end-to-end/src/custom-viz/GeolocationTypes';
import { formatDateToYYYYMMDD } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDateRangeFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getListValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { GoogleMapComponent } from 'apps/sigma2-ui/src/components/GoogleMap';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { getGeolocationFiltersFromCustomViz } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';

export const GeolocationStatefulComponent = () => {
  const { myObservation } = useObservation();
  const { selectedOrganizationId } = useUserObservationOrgAndDbIds();
  const { myUser, userAuthenticatedApiCall } = useUser();

  const context = useCustomViz();
  const { doViz } = context;

  const shareLinkRawData = useShareLinkSearchParameter();

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): { customVizRequest: GeolocationRequest | undefined } => {
      if (!myObservation || !selectedOrganizationId) {
        return { customVizRequest: undefined };
      }

      const metrics =
        getListValueFromPrimaryFilterValues({
          primaryFilterKey: 'metrics',
          primaryFilterValues: primaryFilterSelections,
        }) ?? [];

      const dates = getDateRangeFromPrimaryFilterValues({
        primaryFilterValues: primaryFilterSelections,
      });

      const startDate = dates?.startDate ?? new Date();
      const endDate = dates?.endDate ?? new Date();
      const dateRangeType = dates?.dateRangeType ?? 'all';

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: GeolocationRequest = {
        customVizId: 'geolocation',
        inputs: {
          extras: {
            metrics,
            startDate: formatDateToYYYYMMDD(startDate),
            endDate: formatDateToYYYYMMDD(endDate),
            dateRangeType: dateRangeType,
            filters,
          },
          dropdowns: {},
        },
        observationLocationType: myObservation.type,
        organizationId: selectedOrganizationId,
      };

      return {
        customVizRequest,
      };
    },
    [myObservation, selectedOrganizationId],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest || customVizRequest.customVizId !== 'geolocation') {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest],
  );

  const onApplyGeolocation = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const filterPayload = useFilters({
    feature: 'geolocation',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onApplyGeolocation,
    getCustomVizRequest,
    analysisName: undefined,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (!myObservation || !request || request.customVizId !== 'geolocation') {
        return;
      }

      const selections = getGeolocationFiltersFromCustomViz({
        customVizRequest: request,
        myObservation,
      });

      if (!selections) {
        return;
      }

      const { primaryFilterSelections, secondaryFilterSelections } = selections;

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [myObservation, onAnalyzeWithParams, filterPayload],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: GeolocationRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const topFilterBarItems = useMemo(() => {
    return filterPayload.items.filter(
      (item) => item.key !== 'primary:dateRange',
    );
  }, [filterPayload.items]);

  const bottomFilterBarItems = useMemo(() => {
    const selection = getDateRangeFromPrimaryFilterValues({
      primaryFilterValues: filterPayload.primaryFilterSelections,
    });

    const from = selection?.startDate;
    const to = selection?.endDate;
    const selectedPeriod = selection?.dateRangeType;

    return [
      ...filterPayload.items.filter((item) => item.key === 'primary:dateRange'),
      {
        key: 'dateRangeDisplay',
        content: () => {
          const formattedRange =
            selectedPeriod === 'all'
              ? 'All'
              : from && to
                ? `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`
                : 'No date range selected';

          return (
            <div
              className="flex items-center justify-center h-full"
              style={{
                minHeight: '40px',
                padding: '0 8px',
              }}
            >
              <S2Typography
                variant="body-6"
                translatedContent={formattedRange}
                textColorVariant="gray-600"
              />
            </div>
          );
        },
        menu: () => null,
      },
    ];
  }, [filterPayload.items, filterPayload.primaryFilterSelections]);

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <div>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />
      <Spacing direction="vertical" size="md" className="min-h-96">
        <HorizontalFilterBar
          items={topFilterBarItems}
          slotRight={
            <DataRefreshIndicator
              status={context.status}
              resultCreatedAt={context.resultCreatedAt}
            />
          }
        />
        <HorizontalFilterBar items={bottomFilterBarItems} />

        {lastCustomVizRequest && (
          <GoogleMapComponent
            customVizRequest={lastCustomVizRequest}
            result={context.result}
            status={context.status}
          />
        )}
      </Spacing>
    </div>
  );
};

export const GeolocationComponent = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { myObservation } = useObservation();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  if (
    !myObservation ||
    !selectedDatabaseRevisionId ||
    !selectedDatabaseId ||
    !selectedOrganizationId ||
    !myUser
  ) {
    return <></>;
  }

  return (
    <CustomViz
      observation={myObservation.type}
      organizationId={selectedOrganizationId}
      customVizDefinition={GeolocationCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <GeolocationStatefulComponent />
    </CustomViz>
  );
};
